import React, { useEffect, useMemo, useState } from 'react';
import { Box, HStack, Popover, Portal, Text, VStack } from '@chakra-ui/react';
import { IconButton } from '@/components/ui/icon-button';
import CitedPageChips from '@/components/documents/viewers/CitedPageChips';
import { CitationPreviewEntry, getSourceKey, groupCitationPreviewEntries } from '../citationUtils.ts';
import { LuChevronLeft, LuChevronRight } from 'react-icons/lu';
import { EASE_OUT } from '@/components/ui/motion.ts';

interface CitationPreviewPopoverProps {
	entries: CitationPreviewEntry[];
	children: React.ReactNode;
	initialSourceKey?: string;
	onOpenSource: (sourceIndex: number) => void;
	onOpenFullscreenPage: (entry: CitationPreviewEntry, page: number) => void;
}

const POPOVER_ENTER_MS = 140;

const POPOVER_CONTENT_CSS = {
	transition: `opacity ${POPOVER_ENTER_MS}ms ${EASE_OUT}, transform ${POPOVER_ENTER_MS}ms ${EASE_OUT}`,
	'@starting-style': {
		opacity: 0,
		transform: 'translateY(-2px) scale(0.98)',
	},
	'@media (prefers-reduced-motion: reduce)': {
		transition: 'opacity 120ms ease',
		'@starting-style': {
			opacity: 0,
			transform: 'none',
		},
	},
} as const;

const CitationPreviewPopover: React.FC<CitationPreviewPopoverProps> = (props: CitationPreviewPopoverProps) => {
	const { entries, children, onOpenSource, onOpenFullscreenPage } = props;
	const { initialSourceKey } = props;
	const [open, setOpen] = useState(false);
	const [activeIndex, setActiveIndex] = useState(0);
	const closeTimeoutRef = React.useRef<ReturnType<typeof setTimeout> | null>(null);
	const groups = useMemo(() => groupCitationPreviewEntries(entries), [entries]);
	const initialActiveIndex = useMemo(() => {
		if (!initialSourceKey) return 0;
		const index = groups.findIndex((group) => getSourceKey(group.source) === initialSourceKey);
		return index >= 0 ? index : 0;
	}, [groups, initialSourceKey]);

	useEffect(() => {
		setActiveIndex((index) => Math.min(index, Math.max(groups.length - 1, 0)));
	}, [groups.length]);

	useEffect(() => {
		return () => {
			if (closeTimeoutRef.current) clearTimeout(closeTimeoutRef.current);
		};
	}, []);

	if (groups.length === 0) return children;

	const clearCloseTimeout = () => {
		if (!closeTimeoutRef.current) return;
		clearTimeout(closeTimeoutRef.current);
		closeTimeoutRef.current = null;
	};

	const openPopover = () => {
		clearCloseTimeout();
		setActiveIndex(initialActiveIndex);
		setOpen(true);
	};

	const queueClosePopover = () => {
		clearCloseTimeout();
		closeTimeoutRef.current = setTimeout(() => setOpen(false), 80);
	};

	const activeGroup = groups[Math.min(activeIndex, groups.length - 1)];
	const source = activeGroup.source;
	const citation = activeGroup.citation;
	const file = source.source_file ?? `Source ${citation.ordinal}`;
	const pages = activeGroup.pages;
	const sourceIndex = citation.ordinal - 1;
	const hasMultipleGroups = groups.length > 1;
	const canOpenFullscreen = !!activeGroup.document?.download_url;

	const openFullscreenPage = (page: number) => {
		setOpen(false);
		if (!canOpenFullscreen) {
			onOpenSource(sourceIndex);
			return;
		}
		onOpenFullscreenPage(
			{ source: activeGroup.source, citation: activeGroup.citation, document: activeGroup.document },
			page,
		);
	};

	const openActiveSource = (event: React.MouseEvent | React.KeyboardEvent) => {
		event.preventDefault();
		event.stopPropagation();
		setOpen(false);
		onOpenSource(sourceIndex);
	};

	const showPrevious = (event: React.MouseEvent) => {
		event.stopPropagation();
		setActiveIndex((index) => Math.max(0, index - 1));
	};

	const showNext = (event: React.MouseEvent) => {
		event.stopPropagation();
		setActiveIndex((index) => Math.min(groups.length - 1, index + 1));
	};

	const trigger = React.isValidElement(children)
		? React.cloneElement(children, {
				onClick: (event: React.MouseEvent) => {
					setOpen(false);
					children.props.onClick?.(event);
				},
			} as Partial<React.HTMLAttributes<HTMLElement>>)
		: children;

	return (
		<Box
			as="span"
			display="inline-flex"
			alignItems="center"
			lineHeight="1"
			verticalAlign="inherit"
			onMouseEnter={openPopover}
			onMouseLeave={queueClosePopover}
		>
			<Popover.Root
				open={open}
				autoFocus={false}
				restoreFocus={false}
				positioning={{ placement: 'bottom-start', gutter: 4 }}
			>
				<Popover.Trigger asChild>{trigger}</Popover.Trigger>
				<Portal>
					<Popover.Positioner>
						<Popover.Content
							w="280px"
							// Ark's popover recipe otherwise wins over Chakra's radius prop.
							style={{ borderRadius: 'var(--chakra-radii-2xl)' }}
							borderColor="border.default"
							boxShadow="lg"
							bg="surface.default"
							p={0}
							css={POPOVER_CONTENT_CSS}
							onMouseEnter={clearCloseTimeout}
							onMouseLeave={queueClosePopover}
						>
							<Popover.Body p={3}>
								<VStack gap={3} align="stretch">
									<HStack gap={2} align="flex-start">
										<Box minW={0} flex="1">
											<Text
												role="button"
												tabIndex={0}
												fontSize="sm"
												fontWeight="semibold"
												lineClamp={2}
												// Keep the focus outline rounded despite the Text recipe.
												style={{ borderRadius: 'var(--chakra-radii-md)' }}
												cursor="pointer"
												_hover={{ color: 'brand.fg', textDecoration: 'underline' }}
												_focusVisible={{
													outline: '2px solid',
													outlineColor: 'brand.fg',
													outlineOffset: '2px',
												}}
												onClick={openActiveSource}
												onKeyDown={(event) => {
													if (event.key === 'Enter' || event.key === ' ') {
														openActiveSource(event);
													}
												}}
											>
												{file}
											</Text>
										</Box>
										{hasMultipleGroups && (
											<HStack gap={1}>
												<IconButton
													aria-label="Previous source"
													variant="ghost"
													boxSize="28px"
													minW="28px"
													color="fg.muted"
													disabled={activeIndex === 0}
													_hover={{ bg: 'brand.muted', color: 'brand.fg' }}
													_disabled={{ cursor: 'default', opacity: 0.35 }}
													onClick={showPrevious}
												>
													<LuChevronLeft />
												</IconButton>
												<IconButton
													aria-label="Next source"
													variant="ghost"
													boxSize="28px"
													minW="28px"
													color="fg.muted"
													disabled={activeIndex === groups.length - 1}
													_hover={{ bg: 'brand.muted', color: 'brand.fg' }}
													_disabled={{ cursor: 'default', opacity: 0.35 }}
													onClick={showNext}
												>
													<LuChevronRight />
												</IconButton>
											</HStack>
										)}
									</HStack>
									{pages.length > 0 && (
										<CitedPageChips pages={pages} onOpenPage={openFullscreenPage} />
									)}
								</VStack>
							</Popover.Body>
						</Popover.Content>
					</Popover.Positioner>
				</Portal>
			</Popover.Root>
		</Box>
	);
};

export default CitationPreviewPopover;
